(ns ui.components.card.kanban
  "The card of a Kanban board. It draws the DOM of the Kanban card of the UX
   mockup (ux-overhaul.vercel.app/dealflow). The class names come from the
   generated `ui.components.card.zapkit-styles`. `dom-overrides` has the
   classes that the mockup uses and that the generated styles do not have."
  (:require
   [common.localization :as loc]
   [ui.components.card.zapkit-styles :as styles]
   [ui.components.icon-button.zapkit-styles :as icon-button-styles]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that the mockup uses and the generated styles do not have. Each class
   string is copied from the DOM of the mockup. Each entry names the upstream
   key that it replaces or extends. When ZapKit has a class, use the generated
   class and delete the entry here. Do not edit a generated class string.

   `:fade` replaces the transition classes of `[:shell :base]`.
   `:selected` replaces the background classes of the compound variant of
   `[:shell :compound-variants]` for a kanban card that is not selected.
   `:status-bar-container`, `:status-band` and `:status-colors` replace
   `[:status-bar :base]` and `[:status-bar :status]`. The mockup bar is a band
   inside a container. ZapKit draws the bar as the top border of the container,
   which makes a curve. ZapKit 2.0.0 moved the token `card-status-border-width` from the
   spacing scale to the border widths, so there is no `h-` class for it, and the band reads
   the variable. The mockup colors are `card-status-*`. ZapKit renamed
   them to `status-*-bar`. The values are the same.
   `:title-wrap` and `:title-text` wrap the text of `[:title :base]`. The title
   has one line and an ellipsis. The right padding keeps room for the menu.
   `:title` extends `[:title :base]` with `min-w-0`, so that a long title gets an
   ellipsis. ZapKit has the variant `[:title :truncate]` for this. It puts `truncate`
   on the title element, and the title here has an inner wrap with its own ellipsis
   (`:title-wrap` and `:title-text`), so the card keeps only `min-w-0`.
   `:title-wrap` and `:title-text` also have `leading-[inherit]`. The old global
   style sheet sets `line-height: 1.25` on every element, so the spans of the
   title would not have the line height of the title. The spans of the mockup
   have it.
   `:gap` extends `[:kanban-layout :base]`, which has `gap-0`. The mockup card
   has both `gap-0` and `gap-card-gap`. `tailwind-merge` does not know
   `gap-card-gap`, so it keeps both, and `gap-card-gap` wins in the style sheet
   of the mockup. The card of the mockup shows 6 px between the status bar and
   the content. This class gives the same result, and it wins in any style sheet
   order. Delete it when the designer decides that `gap-0` is right.
   `:footer` extends `[:footer :base]` with a gap of 8 px, so the two sides do not
   touch. ZapKit 2.1.0 draws the footer as the date, the counters and the avatar.
   Each of them has `shrink-0` and does not truncate, so the footer holds short
   values only. A long value is a row of the card, see `ui.view.opensearch-cards.view`.
   `:menu-position` and `:menu-anchor` have no upstream key. They place the menu
   on the top right of the card, as the mockup does. The trigger is the compact
   default IconButton of ZapKit, see `menu-trigger-class`. The mockup draws a
   hand written button with the tokens `close-btn-*`, which ZapKit removed."
  {:fade                 "tw2-transition-opacity tw2-duration-200 tw2-ease-out"
   :selected             "tw2-bg-table-row-bg-selected hover:tw2-bg-table-row-bg-selected"
   :status-bar-container "tw2-h-card-status-bar-height tw2-w-full tw2-mb-card-status-bar-offset tw2-rounded-card"
   :status-band          "tw2-h-[var(--card-status-border-width)] tw2-w-full tw2-rounded-t-card"
   :status-colors        {:won      "tw2-bg-status-success-bar"
                          :on-track "tw2-bg-status-on-hold-bar"
                          :lost     "tw2-bg-status-lost-bar"}
   :title                "tw2-min-w-0"
   :title-wrap           "tw2-flex tw2-min-w-0 tw2-flex-col tw2-items-start tw2-gap-1.5 tw2-pr-5 tw2-leading-[inherit]"
   :title-text           "tw2-inline-block tw2-max-w-full tw2-truncate tw2-leading-[inherit]"
   :footer               "tw2-gap-2"
   :gap                  "!tw2-gap-card-gap"
   :menu-position        "tw2-absolute tw2-right-3 tw2-top-[1.125rem] tw2-z-10"
   :menu-anchor          "tw2-relative tw2-shrink-0"})

(def known-statuses
  #{:won :on-track :lost})

(defn slot-class
  "Return the base classes of a slot of the generated card styles."
  [slot]
  (get-in styles/card [slot :base]))

(defn compound-class
  "Return the classes of the compound variant of a slot that has exactly the
   variant values `variants`, or nil. `slot-styles` is a slot of a generated
   styles map."
  [slot-styles variants]
  (some (fn [compound]
          (when (= variants (dissoc compound :class-name))
            (:class-name compound)))
        (:compound-variants slot-styles)))

(defn menu-trigger-class
  "Return the classes of the trigger of the menu: the default IconButton of
   ZapKit in its compact size."
  []
  (let [root (:root icon-button-styles/icon-button)]
    (uu/cm (:base root)
           (get-in root [:variant :default])
           (get-in root [:compact :true])
           (compound-class root {:variant "default"
                                 :compact true}))))

(defn status-bar-classes
  "Return a map with the `:container` and the `:band` classes of the status bar
   of a card, or nil for the status `:no-status`. A status that the card does
   not know has the bar of `:won`, the default."
  [status]
  (when-not (= :no-status status)
    {:container (:status-bar-container dom-overrides)
     :band      (str (:status-band dom-overrides)
                     " "
                     (get-in dom-overrides [:status-colors (get known-statuses status :won)]))}))

(defn card-class
  "Return the classes of the card. ZapKit puts the shell, the kanban layout and
   the interactive state in this order. `uu/cm` resolves the conflicts as the
   `cn` function of ZapKit does."
  [{:keys [selected? full-width?]}]
  (uu/cm (slot-class :shell)
         (get-in styles/card [:shell :variant :kanban])
         (get-in styles/card [:shell :interactive :true])
         (compound-class (:shell styles/card) {:variant     "kanban"
                                               :interactive true
                                               :selected    false})
         (when full-width?
           "tw2-w-full")
         (:fade dom-overrides)
         (when selected?
           (:selected dom-overrides))
         (slot-class :kanban-layout)
         (:gap dom-overrides)))

(defn menu
  "The menu of the actions of a card. `items` is a vector of menu items, see
   `ui.components.radix-ui.dropdown-menu.view`."
  [{:keys [items]}]
  [:div
   {:class           (:menu-position dom-overrides)
    ;; React events bubble out of the portal of the menu. Without this stop,
    ;; a click on a menu item would also select the card, and a double click
    ;; would open it.
    :on-click        dg/stop-propagation
    :on-double-click dg/stop-propagation}
   [:div {:class (:menu-anchor dom-overrides)}
    [dropdown-menu/view
     {:trigger-as-child [:button
                         {:type       "button"
                          :aria-label (loc/tr :actions)
                          :class      (menu-trigger-class)}
                         [icons/ellipsis-vertical-icon
                          {:class       (get-in icon-button-styles/icon-button [:icon :size :sm])
                           :aria-hidden true}]]
      :items            items
      :props            {:align "end"}}]]])

(defn rows-view
  "The data rows of a card. A row has a label and a value. `:show-labels?` false
   shows the value only."
  [{:keys [rows show-labels?]}]
  [:div {:class (slot-class :attribute-list)}
   (for [row rows]
     ^{:key (str "row-" (:key row))}
     [:div {:class (slot-class :attribute-row)}
      (when show-labels?
        [:p {:class (slot-class :attribute-label)}
         (or (:label row) "")])
      [:p {:class (slot-class :attribute-value)}
       (:value row)]])])

(defn footer-item-view
  "One small field of the footer. `:date?` true puts a calendar icon before the
   text. `:title` is the tooltip of the field. `left?` true draws the item as
   the first item of the footer. Other items are counters of the right side."
  [{:keys [text title date? left?]}]
  [:div
   {:class (slot-class (if left?
                         :meta
                         :count))
    :title title}
   (when date?
     [icons/calendar-icon
      {:class       (slot-class :icon)
       :aria-hidden true}])
   [:p {:class (slot-class :meta-text)}
    text]])

(defn footer-view
  "The footer of a card. `items` is a vector of small fields, see
   `footer-item-view`. The first item is on the left. The other items are on the
   right."
  [{:keys [items]}]
  (let [[left-item & right-items] items]
    [:div {:class (uu/cm (slot-class :footer)
                         (:footer dom-overrides))}
     [footer-item-view (assoc left-item :left? true)]
     (when (seq right-items)
       [:div {:class (slot-class :footer-right)}
        (map-indexed (fn [idx item]
                       ^{:key (str "footer-" idx)}
                       [footer-item-view item])
                     right-items)])]))

(defn view
  "Render a card of a Kanban board.

   `:title` is the text of the header.
   `:status` is `:won`, `:on-track`, `:lost` or `:no-status`. The default is the
   bar of `:won`.
   `:rows` is a vector of maps with `:key`, `:label` and `:value`.
   `:show-labels?` false hides the labels of the rows.
   `:footer` is a vector of maps with `:text`, `:title` and `:date?`, one for
   each small field. The fields must be short, because the footer does not
   truncate. An empty vector or nil gives no footer.
   `:menu-items` is a vector of menu items. The card has no menu without items.
   `:selected?` and `:collapsed?` set the state of the card. A collapsed card
   has the header only.
   `:full-width?` fills the width of the parent. A card that has no `:width`
   also fills it.
   `:card-id` is the value of the attribute `data-card-id`.
   `:on-click` and `:on-double-click` handle the events of the card.
   `:on-title-click` handles a click on the header."
  [{:keys [title status rows show-labels? footer menu-items selected? collapsed?
           full-width? width card-id on-click on-double-click on-title-click]}]
  (let [bar-classes (status-bar-classes status)
        full-width? (or full-width?
                        (nil? width))]
    [:div
     {:class           (card-class {:selected?   selected?
                                    :full-width? full-width?})
      :style           (when-not full-width?
                         {:width width})
      :data-card-id    (some-> card-id str)
      :on-click        on-click
      :on-double-click on-double-click}
     (when bar-classes
       [:div {:class (:container bar-classes)}
        [:div {:class (:band bar-classes)}]])
     [:div
      {:class (uu/cm (slot-class :content)
                     (get-in styles/card [:content :status-bar (if bar-classes
                                                                 :true
                                                                 :false)]))}
      [:div
       {:class    (slot-class :header)
        :on-click on-title-click}
       [:div {:class (slot-class :title-group)}
        [:p {:class (uu/cm (slot-class :title)
                           (:title dom-overrides))}
         [:span {:class (:title-wrap dom-overrides)}
          [:span {:class (:title-text dom-overrides)}
           title]]]]]
      (when (and (not collapsed?)
                 (seq rows))
        [rows-view {:rows         rows
                    :show-labels? show-labels?}])
      (when (and (not collapsed?)
                 (seq footer))
        [footer-view {:items footer}])]
     (when (seq menu-items)
       [menu {:items menu-items}])]))
